import type { ReportTrendLineData } from "@repo/app-types/entities/report";
import { ChartSkeleton } from "@repo/ui/Skeletons/skeletons";
import { DateTime } from "luxon";
import { useMemo, type FC, type ReactNode } from "react";
import {
  CartesianGrid,
  Dot,
  LabelList,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { BaseCard } from "./baseCard.js";
import { BaseTooltipContent } from "./baseTooltipContent.js";
import { renderValueAsString } from "./values.js";

export interface TrendLineCardProps {
  title: ReactNode;
  data?: ReportTrendLineData;
  rechartSyncId?: string;
}

const seriesColors: readonly string[] = [
  "var(--color-blue-500)",
  "var(--color-green-500)",
  "var(--color-amber-500)",
  "var(--color-red-500)",
  "var(--color-purple-500)",
];

const SERIES_LABEL_WIDTH = 60;
const SERIES_LABEL_CHARS = 9;

export const TrendLineCard: FC<TrendLineCardProps> = (props) => {
  const { data } = props;

  const valueTypes = useMemo(
    () =>
      data ? [...new Set(data.series.map((s) => s.valueType))] : undefined,
    [data],
  );

  return (
    <BaseCard title={props.title}>
      {data ? (
        <>
          {data.series.length === 0 ||
          data.series.every((s) => s.values.every((v) => v === null)) ? (
            <div className="flex flex-auto items-center justify-center py-12 text-center text-sm text-gray-400">
              No data
            </div>
          ) : (
            <div className="-m-[5px] mt-2">
              <LineChart
                syncId={props.rechartSyncId}
                syncMethod="value"
                style={{
                  width: "100%",
                  aspectRatio: "2 / 1",
                }}
                margin={{
                  top: 5,
                  right: 5 + SERIES_LABEL_WIDTH,
                  bottom: 5,
                  left: 5,
                }}
                responsive
                data={(data.series[0]?.values ?? []).map((_, i) => {
                  const point: Record<string, any> = {
                    date: DateTime.fromISO(data.minDate)
                      .plus({ days: i })
                      .toISODate(),
                  };

                  for (let j = 0; j < data.series.length; j++) {
                    const series = data.series[j];
                    if (series === undefined) continue;
                    point[`series${j}`] = series.values[i];
                    point[`series${j}Label`] = series.label;
                    point[`series${j}ValueType`] = series.valueType;
                  }

                  return point;
                })}
              >
                <CartesianGrid
                  stroke="var(--color-gray-200)"
                  strokeWidth={1}
                  vertical={false}
                  yAxisId={valueTypes?.at(0)}
                />
                <XAxis
                  dataKey="date"
                  stroke="none"
                  tick={{
                    fontSize: "0.75rem",
                    fill: "var(--color-gray-500)",
                  }}
                  ticks={Array.from({ length: data.periodCount }, (_, i) => {
                    return DateTime.fromISO(data.minDate)
                      .plus({ days: data.periodLengthDays * i })
                      .toISODate()!;
                  })}
                  tickFormatter={(value: any) => {
                    const day = DateTime.fromISO(value);
                    return day.toFormat("M/d");
                  }}
                />
                {valueTypes?.map((valueType, index) => (
                  <YAxis
                    key={valueType}
                    yAxisId={valueType}
                    type="number"
                    width="auto"
                    stroke="none"
                    tick={{
                      fontSize: "0.75rem",
                      fill: "var(--color-gray-500)",
                    }}
                    tickFormatter={(value) =>
                      renderValueAsString({
                        value,
                        valueType,
                      })
                    }
                    domain={["auto", "auto"]}
                    allowDataOverflow
                  />
                ))}

                {data.series.map((s, i) => {
                  const colorIndex = i % seriesColors.length;
                  return (
                    <Line
                      key={`series_${i}`}
                      yAxisId={s.valueType}
                      type="monotone"
                      dataKey={`series${i}`}
                      name={s.label}
                      stroke={seriesColors[colorIndex]}
                      strokeWidth={1.5}
                      dot={(props) => {
                        if (props.index % data.periodLengthDays !== 0) {
                          return null;
                        }

                        return (
                          <Dot
                            cx={props.cx}
                            cy={props.cy}
                            r={2}
                            stroke="none"
                            fill={seriesColors[colorIndex]}
                          />
                        );
                      }}
                    >
                      <LabelList
                        position="right"
                        valueAccessor={(point, index) => {
                          const indexOfLastNonNull = s.values.findLastIndex(
                            (v) => v !== null,
                          );

                          if (
                            indexOfLastNonNull === -1 ||
                            index !== indexOfLastNonNull
                          ) {
                            return null;
                          }

                          const value = s.label;
                          return value.length > SERIES_LABEL_CHARS
                            ? value.slice(0, SERIES_LABEL_CHARS - 1) + "…"
                            : value;
                        }}
                        fill={seriesColors[colorIndex]}
                        fontSize="0.625rem"
                        fontWeight="600"
                        stroke="white"
                        strokeWidth="2"
                        paintOrder="stroke"
                      />
                    </Line>
                  );
                })}
                <Tooltip content={TooltipContent} />
              </LineChart>
            </div>
          )}
        </>
      ) : (
        <div style={{ aspectRatio: 1.618, maxHeight: "70vh" }}>
          <ChartSkeleton />
        </div>
      )}
    </BaseCard>
  );
};

const TooltipContent = (props: TooltipContentProps) => {
  const entries = props.payload;

  if (!props.active || !entries?.length) {
    return null;
  }

  const firstEntry = entries[0];
  if (firstEntry === undefined) {
    return null;
  }

  return (
    <BaseTooltipContent
      label={
        <>
          {DateTime.fromISO(firstEntry.payload.date).toLocaleString(
            DateTime.DATE_MED,
          )}
        </>
      }
      entries={entries.map((entry) => {
        if (typeof entry.dataKey !== "string") {
          return {
            color: entry.color,
            value: null,
          };
        }

        const labelKey = `${entry.dataKey}Label`;
        const valueKey = entry.dataKey;
        const valueTypeKey = `${entry.dataKey}ValueType`;

        return {
          color: entry.color,
          label: entry.payload[labelKey] ?? null,
          value: renderValueAsString({
            value: entry.payload[valueKey] ?? null,
            valueType: entry.payload[valueTypeKey],
          }),
        };
      })}
    />
  );
};
